import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthClient } from '@/lib/authClient';
import { useConsumeInvitation } from '@/hooks/organization/use-consume-invitation';
import { useOrganizationValidation } from '@/hooks/organization/use-signup-token-validation';

// This page exists to handle the redirect from the auth server.
// There are two possible cases:
// 1) The user is being redirected to create an organization
// 2) The user is being redirected to join an organization
// We handle both cases here. We want a centralized place to handle the redirect before
// letting users connect their mobile devices.
function OrganizationRedirectPage() {
  const navigate = useNavigate();
  const { session } = useAuthClient();
  const actedRef = useRef(false);

  const [params] = useSearchParams();
  const orgCreateToken = params.get('orgCreateToken');
  const signupToken = params.get('signupToken');

  const { consume } = useConsumeInvitation();
  const validation = useOrganizationValidation({
    orgCreateToken,
    signupToken,
  });

  useEffect(() => {
    if (!session.data) return;
    if (actedRef.current) return;
    if (validation.type === 'invalid') return;
    if (validation.isLoading) return;

    if (validation.type === 'token') {
      if (validation.isLoading) return;
      navigate(
        `/create-organization?orgCreateToken=${encodeURIComponent(
          validation.orgCreateToken
        )}`,
        { replace: true }
      );
    }

    if (validation.type === 'invite') {
      (async () => {
        actedRef.current = true;
        await consume(validation.signupToken);
        navigate('/register-device', { replace: true });
      })();
    }
  }, [session.data, validation, consume, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white">
      <div className="flex items-center space-x-2 text-slate-600">
        {validation.type === 'invalid' ? (
          <div>Invalid link. Contact support.</div>
        ) : (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-400" />
        )}
      </div>
    </div>
  );
}

export default OrganizationRedirectPage;
